import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Two UI modes:
 *   "playful" — full Resmo: orange wordmark, mascot, monster microcopy
 *   "promo"   — ProMo: navy wordmark, no mascot, restrained microcopy
 *
 * The mode is applied via `data-mode` on <html> so CSS variable overrides
 * cascade automatically (orange → navy primary, etc.).
 */

const STORAGE_KEY = "resmo:ui_mode";
const VALID = ["playful", "promo"];

const ThemeContext = createContext({
  mode: "playful",
  isPro: false,
  setMode: () => {},
  toggleMode: () => {},
});

const applyMode = (m) => {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-mode", m);
    // Also swap the document title so the browser tab matches the brand
    document.title = m === "promo"
      ? "ProMo · Career Strategy for Senior Professionals"
      : "Resmo · Unleash the Resume Beast";
  }
};

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [mode, setModeState] = useState(() => {
    if (typeof window === "undefined") return "playful";
    const cached = window.localStorage.getItem(STORAGE_KEY);
    return VALID.includes(cached) ? cached : "playful";
  });

  // Apply on every change
  useEffect(() => {
    applyMode(mode);
  }, [mode]);

  // When auth resolves, sync from server preferences
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/account/preferences");
        const remote = VALID.includes(data?.ui_mode) ? data.ui_mode : "playful";
        if (remote !== mode) {
          setModeState(remote);
          window.localStorage.setItem(STORAGE_KEY, remote);
        }
      } catch {
        /* ignore */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setMode = useCallback(async (next) => {
    if (!VALID.includes(next)) return;
    setModeState(next);
    window.localStorage.setItem(STORAGE_KEY, next);
    if (user) {
      try {
        await api.patch("/account/preferences", { ui_mode: next });
      } catch {
        /* keep local change even if persist fails */
      }
    }
  }, [user]);

  const toggleMode = useCallback(() => {
    setMode(mode === "promo" ? "playful" : "promo");
  }, [mode, setMode]);

  return (
    <ThemeContext.Provider value={{ mode, isPro: mode === "promo", setMode, toggleMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
